import camelCase from 'lodash/camelCase';
import { join } from 'path';
import merge from 'lodash/merge';

import { storage, doesFileExists } from '../../firebase';

const storageRef = storage.ref();

const prependWithPrefix = (name, prefix) =>
  prefix ? camelCase(`${prefix}_${name}`) : name;

/**
 * Use when you have a field in the document that points to some uploaded file.
 *
 * Adding this plugin to a module will populate the following values:
 *
 * state variables populated:
 *  1. `<fieldName>Uploading` - uploading status
 *  1. `<fieldName>uploadProgress` - upload progress in percent
 *  1. `<fieldName>uploadError` - will contain the upload error if it fails.
 *  1. `<fieldName>uploadURL` - will have the final gcs URL once the upload
 * task is resolved.
 *
 * actions populated:
 *  1. `async upload<fieldName>` - action used to upload the file. this method
 * expects a hash with two compulsory keys `pathPrefix` and `file`. However if
 * anything else is needed to generate custom upload path or meta attributes
 * those can be passed and can used in the `getUploadPath` and `getMeta` methods
 * to generate those.
 *
 * @param {*} fieldName - used for generating state variables and mutation
 * names, using the actual field name itself would be appropriate, unless there
 * is a reason not to. This plugin would work even if the fieldName is an empty
 * string.
 *
 * @param {*} [getUploadPath] - an optional function to calculate final path to
 * upload file to, will get complete payload from function `upload<fieldName>`
 * as an argument. By default `adminUploads/<pathPrefix>/<file.name>` is used.
 *
 * @param {*} [getMeta] - an optional method to add additional meta data to the
 * file being uploaded if required.
 *
 * PS: This plugin can be included in the same module multiple times, in case a
 * document has more than one field that needs uploading and needs to have
 * separate uploading statuses to be maintained. However this plugin is
 * completely independent of documents or fields all it does is upload a file
 * and return url.
 */

export default function uploadable(fieldName, getUploadPath, getMeta) {
  return function(module) {
    const mutationPrefix = `${module.name.toUpperCase()}${
      fieldName ? `_${fieldName.toUpperCase()}` : ''
    }`;

    const UPLOAD_INIT = `${mutationPrefix}_UPLOAD_INIT`;
    const UPLOAD_PROGRESS = `${mutationPrefix}_UPLOAD_PROGRESS`;
    const UPLOAD_DONE = `${mutationPrefix}_UPLOAD_DONE`;
    const UPLOAD_ERROR = `${mutationPrefix}_UPLOAD_ERROR`;

    module.addMutationTypes([
      UPLOAD_INIT,
      UPLOAD_PROGRESS,
      UPLOAD_DONE,
      UPLOAD_ERROR
    ]);

    const uploading = prependWithPrefix('uploading', fieldName);
    const uploadProgress = prependWithPrefix('uploadProgress', fieldName);
    const uploadURL = prependWithPrefix('uploadURL', fieldName);
    const uploadError = prependWithPrefix('uploadError', fieldName);

    const state = {
      [uploading]: false,
      [uploadProgress]: 0,
      [uploadError]: null,
      [uploadURL]: null
    };

    const mutations = {
      [UPLOAD_INIT](state) {
        state[uploading] = true;
        state[uploadURL] = null;
        state[uploadProgress] = 0;
      },
      [UPLOAD_PROGRESS](state, progress) {
        state[uploadProgress] = progress;
      },
      [UPLOAD_DONE](state, URL) {
        state[uploading] = false;
        state[uploadURL] = URL;
      },
      [UPLOAD_ERROR](state, error) {
        state[uploading] = false;
        state[uploadError] = error;
      }
    };

    const actions = {
      async [camelCase(
        `upload_${module.moduleName}${fieldName ? `_${fieldName}` : ''}`
      )]({ commit, state }, payload) {
        // const file, overwrite = payload;
        if (state.uploading) {
          return new Promise.reject('An upload is already in progress!');
        }

        const uploadPath = (getUploadPath ||
          (({ pathPrefix, file }) =>
            join('adminUploads', pathPrefix, file.name)))(payload);

        const fileRef = storageRef.child(uploadPath);

        return await new Promise(async (resolve, reject) => {
          commit(UPLOAD_INIT);

          if ((await doesFileExists(fileRef)) && !payload.overwrite) {
            const error =
              'File with that name already exists on the server, check `overwrite` to overwrite';
            commit(UPLOAD_ERROR, error);
            reject(error);
            return;
          }

          var uploadTask = storageRef
            .child(uploadPath)
            .put(payload.file, getMeta ? getMeta(payload) : undefined);

          uploadTask.on(
            'state_changed',
            snapshot => {
              var progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              commit(UPLOAD_PROGRESS, progress);
            },
            error => {
              commit(UPLOAD_ERROR, error);
              reject(error);
            },
            () => {
              commit(UPLOAD_DONE, `gcs:///${uploadPath}`);
              resolve();
            }
          );
        });
      }
    };

    return merge(module, { state, mutations, actions });
  };
}
